// src/components/agent/session/approvalHydration.js
//
// The ONE strict hydrator for durable Agent approvals (email spec §7.14).
//
// Approvals are persisted at
// `users/{company}/agents/{agent}/pending_approvals/{agent_message_id}_{cycle}`.
// The browser sees that same record from two places — the live
// `approval_required` Server-Sent Event and the Firestore subscription — and
// both are hydrated here, by this function, into one model. There is no
// live-only path and no "details unavailable" reconstruction: an approval that
// cannot be described completely is rendered as non-approvable, never as an
// approvable card with missing detail.
//
// Persisted record (store contract §6.5; the live event carries the same field
// names for the subset it sends):
//   { state, turn_id, agent_message_id, cycle, provider_cycle_envelope,
//     requested_at, expires_at, decided_at?, executed_at?,
//     calls: [ { tool_use_id, tool_name, tool_args, gate, reason, decision,
//                approval_summary, function_version } ] }
// The live event sends { agent_message_id, cycle, gated_calls: [ { tool_use_id,
// tool_name, gate, approval_summary } ] } — the same per-call field names, so
// one normalisation covers both.
//
// Hydrated model:
//   { kind: 'approval', approvalId, messageId, cycle, state,
//     calls:    [ { key, toolUseId, toolName, gate, decision, approvalSummary } ],
//     askCalls: [ …the subset whose ORIGINAL gate is 'ask'… ],
//     decisionIds: [toolUseId…],   // the complete ASK set a decision covers
//     decisionOutcome,             // null | 'approved' | 'denied' | 'closed'
//     error }                      // null, or one bounded non-approvable reason

const APPROVAL_STATES = ['pending', 'decisions_recorded', 'executing', 'completed'];
const GATES = ['allow', 'ask', 'deny'];
const DECISIONS = ['approved', 'denied', 'superseded', 'expired'];

// Bounded, user-readable reasons a record cannot be approved in the browser.
const ERRORS = {
  malformed: 'This approval record is malformed, so it cannot be approved here.',
  missingDetail: 'Decode could not load the full details of this request, so it cannot be approved here.',
  unsupported: 'This request includes an action this version of Decode cannot describe, so it cannot be approved here.',
};

// The backend addresses a parked cycle by approval_id = "{messageId}_{cycle}"
// (store contract), so every source rebuilds the same decision-endpoint id.
export const approvalIdFor = (messageId, cycle) => `${messageId}_${cycle}`;

const isFilledString = (value) => typeof value === 'string' && value.trim().length > 0;

const hasExactKeys = (value, keys) =>
  !!value
  && typeof value === 'object'
  && !Array.isArray(value)
  && Object.keys(value).length === keys.length
  && keys.every((key) => key in value);

const isFilledStringArray = (value) =>
  Array.isArray(value) && value.length > 0 && value.every(isFilledString);

// Validate and normalise one tool's structured approval_summary. Returns the
// normalised summary, or null when the summary is absent, malformed, or belongs
// to a tool this build cannot describe. The accepted shapes mirror the backend
// validator in `harness/sinks.py` exactly (spec §2.5).
export function normalizeApprovalSummary(toolName, summary) {
  if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return null;
  if (summary.tool !== toolName) return null;

  if (toolName === 'send_email') {
    // The body joined the summary later: a record parked before then has
    // none and still describes a decidable email, so it is optional for
    // display. When present it must be real text.
    const { body, ...rest } = summary;
    if (!hasExactKeys(rest, ['tool', 'recipients', 'subject', 'attachments'])) return null;
    if (body !== undefined && !isFilledString(body)) return null;
    const { recipients, subject, attachments } = rest;
    if (!Array.isArray(recipients) || recipients.length === 0) return null;
    if (!recipients.every((recipient) => (
      hasExactKeys(recipient, ['display_name', 'email'])
      && isFilledString(recipient.display_name)
      && isFilledString(recipient.email)
    ))) return null;
    if (!isFilledString(subject) || !isFilledStringArray(attachments)) return null;
    return {
      tool: 'send_email',
      recipients: recipients.map((recipient) => ({
        displayName: recipient.display_name,
        email: recipient.email,
      })),
      subject,
      body: body === undefined ? null : body,
      attachments: [...attachments],
    };
  }

  if (toolName === 'run_company_data_function') {
    if (!hasExactKeys(summary, ['tool', 'function_name', 'outputs'])) return null;
    const { function_name: functionName, outputs } = summary;
    if (!isFilledString(functionName)) return null;
    if (!Array.isArray(outputs) || outputs.length === 0) return null;
    if (!outputs.every((output) => (
      hasExactKeys(output, ['node_name', 'output_type', 'recipients', 'subject', 'attachments'])
      && isFilledString(output.node_name)
      && output.output_type === 'email'
      && isFilledStringArray(output.recipients)
      && isFilledString(output.subject)
      && isFilledStringArray(output.attachments)
    ))) return null;
    return {
      tool: 'run_company_data_function',
      functionName,
      outputs: outputs.map((output) => ({
        nodeName: output.node_name,
        outputType: output.output_type,
        recipients: [...output.recipients],
        subject: output.subject,
        attachments: [...output.attachments],
      })),
    };
  }

  return null;
}

const buildModel = ({ approvalId, messageId, cycle, state, calls, error }) => {
  const askCalls = calls.filter((call) => call.gate === 'ask');
  const decided = askCalls.length > 0 && askCalls.every((call) => call.decision != null);
  let decisionOutcome = null;
  if (decided) {
    if (askCalls.every((call) => call.decision === 'approved')) decisionOutcome = 'approved';
    else if (askCalls.every((call) => call.decision === 'denied')) decisionOutcome = 'denied';
    else decisionOutcome = 'closed'; // superseded/expired, or a mixed decision
  }
  return {
    kind: 'approval',
    approvalId,
    messageId,
    cycle,
    state,
    calls,
    askCalls,
    // A decision always covers the complete validated ASK set — never the
    // subset that happened to hydrate.
    decisionIds: error ? [] : askCalls.map((call) => call.toolUseId),
    decisionOutcome,
    error: error || null,
  };
};

/**
 * Hydrate one persisted PendingApprovalRecord — from the live
 * `approval_required` event or from a `pending_approvals` document snapshot —
 * into the render/decision model.
 *
 * Returns null when the value is not an approval record at all (so callers can
 * ignore it), and a model carrying `error` when it IS an approval record that
 * cannot be safely approved in the browser.
 */
export function hydrateApprovalRecord(source) {
  if (!source || typeof source !== 'object') return null;

  const messageId = source.agent_message_id;
  const cycle = source.cycle;
  if (!isFilledString(messageId)) return null;
  if (typeof cycle !== 'number' || !Number.isInteger(cycle) || cycle < 0) return null;

  const approvalId = approvalIdFor(messageId, cycle);
  const identity = { approvalId, messageId, cycle };
  // The document id IS "{agent_message_id}_{cycle}"; a disagreement means the
  // record is not the one it claims to be.
  if (source.id != null && source.id !== approvalId) {
    return buildModel({ ...identity, state: 'pending', calls: [], error: ERRORS.malformed });
  }

  const state = source.state == null ? 'pending' : source.state;
  if (!APPROVAL_STATES.includes(state)) {
    return buildModel({ ...identity, state: 'pending', calls: [], error: ERRORS.malformed });
  }

  // `calls` (durable) and `gated_calls` (live event) hold the same per-call
  // shape — one normalisation, no live-only path.
  const rawCalls = Array.isArray(source.calls)
    ? source.calls
    : (Array.isArray(source.gated_calls) ? source.gated_calls : null);
  if (!rawCalls || rawCalls.length === 0) {
    return buildModel({ ...identity, state, calls: [], error: ERRORS.malformed });
  }

  const calls = [];
  const seen = new Set();
  let error = null;

  for (const rawCall of rawCalls) {
    if (!rawCall || typeof rawCall !== 'object') {
      error = error || ERRORS.malformed;
      break;
    }
    const toolUseId = rawCall.tool_use_id;
    const toolName = rawCall.tool_name;
    const gate = rawCall.gate;
    const decision = rawCall.decision == null ? null : rawCall.decision;
    if (
      !isFilledString(toolUseId)
      || !isFilledString(toolName)
      || !GATES.includes(gate)
      || (decision !== null && !DECISIONS.includes(decision))
      || seen.has(toolUseId)
    ) {
      error = error || ERRORS.malformed;
      break;
    }
    seen.add(toolUseId);

    // Only ASK calls are ever shown or decided, so only their summaries are
    // validated. ALLOW/DENY calls stay in the model with their original gate
    // and decision so mixed-call state survives replay.
    let approvalSummary = null;
    if (gate === 'ask') {
      approvalSummary = normalizeApprovalSummary(toolName, rawCall.approval_summary);
      if (!approvalSummary) {
        const describable = toolName === 'send_email' || toolName === 'run_company_data_function';
        error = error || (describable ? ERRORS.missingDetail : ERRORS.unsupported);
      }
    }

    calls.push({
      key: `${approvalId}:${toolUseId}`,
      toolUseId,
      toolName,
      gate,
      decision,
      approvalSummary,
    });
  }

  // Partial approval of only the calls that hydrated is forbidden: one bad ASK
  // call makes the whole record non-approvable.
  return buildModel({ ...identity, state, calls, error });
}

const stateRank = (model) => APPROVAL_STATES.indexOf(model?.state);

/**
 * Fold a freshly hydrated record into the one already held for that approval,
 * so the same approval arriving live and durably — in either order — collapses
 * to a single card. Lifecycle never regresses: a stale `pending` frame cannot
 * re-open an approval the durable record has already carried forward.
 */
export function mergeApprovalRecord(existing, incoming) {
  if (!incoming) return existing || null;
  if (!existing) return incoming;
  if (existing.approvalId !== incoming.approvalId) return incoming;
  return stateRank(incoming) < stateRank(existing) ? existing : incoming;
}
